/**
 * Chart of Accounts and vouchers — backed by the Spring Boot API.
 */
import type { CreateAccountInput, CreateVoucherInput, VoucherType, EntryType } from '../../shared/types/coa';
import { api, qs } from './http';

export function applyLineToBalance(typeName: string, entry: EntryType, amount: number): number {
  const debitPositive = typeName === 'Asset' || typeName === 'Expense';
  if (entry === 'debit') return debitPositive ? amount : -amount;
  return debitPositive ? -amount : amount;
}

export const coaMockApi = {
  getAccountTypes: async () => api('/api/finance/account-types'),

  getChartOfAccounts: async (_tenantId?: string) => api('/api/finance/accounts'),

  getLedgerAccounts: async (filters?: { assetOnly?: boolean }) =>
    api(`/api/finance/accounts/ledgers${qs({ assetOnly: filters?.assetOnly || undefined })}`),

  getParentGroups: async (accountTypeId: string) =>
    api(`/api/finance/accounts/groups${qs({ accountTypeId })}`),

  createAccount: async (_tenantId: string, input: CreateAccountInput) =>
    api('/api/finance/accounts', { method: 'POST', body: JSON.stringify(input) }),

  updateAccount: async (_tenantId: string, id: string, input: Partial<CreateAccountInput>) =>
    api(`/api/finance/accounts/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  deleteAccount: async (_tenantId: string, id: string) =>
    api(`/api/finance/accounts/${id}`, { method: 'DELETE' }),

  getVouchers: async (_tenantId?: string) => api('/api/finance/vouchers'),

  createVoucher: async (_tenantId: string, input: CreateVoucherInput, status: string) =>
    api('/api/finance/vouchers', {
      method: 'POST',
      body: JSON.stringify({ ...input, status, action: status === 'pending_approval' ? 'submit' : status === 'approved' ? 'approve' : 'draft' }),
    }),

  submitVoucher: async (id: string) => api(`/api/finance/vouchers/${id}/submit`, { method: 'POST' }),

  approveAndPostVoucher: async (id: string, approvedBy = 'Admin') =>
    api(`/api/finance/vouchers/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: approvedBy }),
    }),

  rejectVoucher: async (id: string) => api(`/api/finance/vouchers/${id}/reject`, { method: 'POST' }),

  saveVoucher: async (
    _tenantId: string,
    input: CreateVoucherInput,
    action: 'draft' | 'submit' | 'approve',
    userName = 'User'
  ) =>
    api('/api/finance/vouchers', {
      method: 'POST',
      body: JSON.stringify({ ...input, action, created_by: userName }),
    }),

  getTrialBalance: async (asOn?: string) => api(`/api/finance/trial-balance${qs({ asOn })}`),

  getLedger: async (accountId: string, from?: string, to?: string) =>
    api(`/api/finance/ledger/${accountId}${qs({ from, to })}`),

  getProfitAndLoss: async () => api('/api/finance/profit-and-loss'),
};

export type CoaMockApi = typeof coaMockApi;
export type { VoucherType };
